import ApiClient from "../client";
import { PaginatedResponse } from "../types";
import {
  ApiCreateCreditAdjustmentPayload,
  ApiCreateCreditProductPayload,
  ApiCreateCreditRedemptionPayload,
  ApiCreditAccountAdmin,
  ApiCreditAccountAdminFilters,
  ApiCreditProduct,
  ApiCreditProductAdmin,
  ApiCreditProductAdminFilters,
  ApiCreditProductAdminListItem,
  ApiCreditProductCodeBatch,
  ApiCreditProductCodeBatchUpload,
  ApiCreditProductPresetImage,
  ApiCreditProductStock,
  ApiCreditRedemption,
  ApiCreditRedemptionAdmin,
  ApiCreditRedemptionAdminFilters,
  ApiCreditRedemptionSticker,
  ApiCreditSystemAdmin,
  ApiCreditTransaction,
  ApiCreditTransactionAdmin,
  ApiCreditTransactionFilters,
  ApiCreditWallet,
  ApiUpdateCreditProductPayload,
  ApiUpdateCreditSystemPayload,
  CreditRewardCategory,
  CreditTransactionDirection,
  ApiCreditAccountStatusChange,
  ApiCreditAccountStatusChangePayload,
  ApiCreditAccountStatusDetail,
} from "../types/credits";

export const getCreditWallet = (client: ApiClient, venueUuid?: string) =>
  client.get<ApiCreditWallet>(
    `/credits/wallet` + (venueUuid ? `?venue=${venueUuid}` : "")
  );

export const getVenueCreditProducts = (client: ApiClient, venueUuid: string) =>
  client.get<ApiCreditProduct[]>(`/venues/${venueUuid}/credit-products`);

export const createCreditRedemption = (
  client: ApiClient,
  venueUuid: string,
  payload: ApiCreateCreditRedemptionPayload
) =>
  client.post<ApiCreditRedemption>(
    `/venues/${venueUuid}/credit-redemptions`,
    payload
  );

export const getCreditRedemptions = (client: ApiClient) =>
  client.get<PaginatedResponse<ApiCreditRedemption>>(`/credits/redemptions`);

export const getCreditRedemption = (
  client: ApiClient,
  redemptionUuid: string
) =>
  client.get<ApiCreditRedemption>(`/credits/redemptions/${redemptionUuid}`);

export const getCreditRedemptionSticker = (
  client: ApiClient,
  stickerId: string
) =>
  client.get<ApiCreditRedemptionSticker>(`/credits/stickers/${stickerId}`);

export const getVenueCreditSystem =(client: ApiClient, venueUuid: string) =>
  client.get<ApiCreditSystemAdmin>(`/venues/${venueUuid}/credit-system`);

export const updateCreditSystem = (
  client: ApiClient,
  systemSlug: string,
  payload: ApiUpdateCreditSystemPayload
) =>
  client.patch<ApiCreditSystemAdmin>(`/credits/systems/${systemSlug}`, payload);

/**
 * A system's rewards as route manager shows them at one venue: every reward
 * for an admin there, or the venue's own rewards for staff.
 */
export const getCreditSystemProducts = (
  client: ApiClient,
  systemSlug: string,
  venueUuid: string,
  filters: ApiCreditProductAdminFilters = {},
  page = 1
) =>
  client.get<PaginatedResponse<ApiCreditProductAdminListItem>>(
    `/credits/systems/${systemSlug}/products`,
    {
      searchParams: {
        venue: venueUuid,
        page: String(page),
        ...(filters.isActive !== undefined
          ? { is_active: String(filters.isActive) }
          : {}),
      },
    }
  );

export const createCreditProduct = (
  client: ApiClient,
  systemSlug: string,
  payload: ApiCreateCreditProductPayload
) =>
  client.post<ApiCreditProductAdmin>(
    `/credits/systems/${systemSlug}/products`,
    payload
  );

export const getCreditProduct = (client: ApiClient, productUuid: string) =>
  client.get<ApiCreditProductAdmin>(`/credits/products/${productUuid}`);

export const updateCreditProduct = (
  client: ApiClient,
  productUuid: string,
  payload: ApiUpdateCreditProductPayload
) =>
  client.patch<ApiCreditProductAdmin>(
    `/credits/products/${productUuid}`,
    payload
  );

export const deleteCreditProduct = (client: ApiClient, productUuid: string) =>
  client.delete(`/credits/products/${productUuid}`);

/** The active preset images, ordered by category and then by their sort order. */
export const getCreditProductPresetImages = (
  client: ApiClient,
  category?: CreditRewardCategory,
  page = 1
) =>
  client.get<PaginatedResponse<ApiCreditProductPresetImage>>(
    `/credits/preset-images`,
    {
      searchParams: {
        page: String(page),
        ...(category ? { category } : {}),
      },
    }
  );

export const getCreditProductVenueStock = (
  client: ApiClient,
  productUuid: string,
  venueUuid: string
) =>
  client.get<ApiCreditProductStock>(
    `/credits/products/${productUuid}/stock/${venueUuid}`
  );

export const setCreditProductVenueStock = (
  client: ApiClient,
  productUuid: string,
  venueUuid: string,
  remaining: number | null
) =>
  client.put<ApiCreditProductStock>(
    `/credits/products/${productUuid}/stock/${venueUuid}`,
    { remaining }
  );

export const getCreditProductCodeBatches = (
  client: ApiClient,
  productUuid: string,
  venueUuid?: string
) =>
  client.get<PaginatedResponse<ApiCreditProductCodeBatch>>(
    `/credits/products/${productUuid}/code-batches`,
    { searchParams: venueUuid ? { venue: venueUuid } : {} }
  );

/**
 * Upload a text or CSV file with one code per line (a CSV's first column).
 * Pass `hasHeader` to skip the file's first row.
 */
export const uploadCreditProductCodeBatch = (
  client: ApiClient,
  productUuid: string,
  venueUuid: string,
  file: Blob,
  filename: string,
  hasHeader = false
) => {
  const body = new FormData();
  body.append("venue", venueUuid);
  body.append("file", file, filename);
  body.append("has_header", String(hasHeader));
  return client.post<ApiCreditProductCodeBatchUpload>(
    `/credits/products/${productUuid}/code-batches`,
    undefined,
    { body }
  );
};

/** Delete a batch's unclaimed codes. The batch stays, marked withdrawn. */
export const withdrawCreditProductCodeBatch = (
  client: ApiClient,
  productUuid: string,
  batchUuid: string
) =>
  client.delete(
    `/credits/products/${productUuid}/code-batches/${batchUuid}`
  );

/** The current user's transaction log, newest first. */
export const getCreditTransactions = (
  client: ApiClient,
  filters: ApiCreditTransactionFilters = {},
  page = 1
) =>
  client.get<PaginatedResponse<ApiCreditTransaction>>(`/credits/transactions`, {
    searchParams: {
      page: String(page),
      ...(filters.system ? { system: filters.system } : {}),
      ...(filters.direction ? { direction: filters.direction } : {}),
    },
  });

/** A system's member accounts, for admins at any of its venues. */
export const getCreditSystemAccounts = (
  client: ApiClient,
  systemSlug: string,
  filters: ApiCreditAccountAdminFilters = {},
  page = 1
) =>
  client.get<PaginatedResponse<ApiCreditAccountAdmin>>(
    `/credits/systems/${systemSlug}/accounts`,
    {
      searchParams: {
        page: String(page),
        ...(filters.search ? { search_q: filters.search } : {}),
        ...(filters.sort ? { sort: filters.sort } : {}),
        ...(filters.status ? { status: filters.status } : {}),
      },
    }
  );

/** A system's redemptions, newest first. Admins see every venue and staff only their own. */
export const getCreditSystemRedemptions = (
  client: ApiClient,
  systemSlug: string,
  filters: ApiCreditRedemptionAdminFilters = {},
  page = 1
) =>
  client.get<PaginatedResponse<ApiCreditRedemptionAdmin>>(
    `/credits/systems/${systemSlug}/redemptions`,
    {
      searchParams: {
        page: String(page),
        ...(filters.venue ? { venue: filters.venue } : {}),
        ...(filters.product ? { product: filters.product } : {}),
        ...(filters.search ? { search_q: filters.search } : {}),
      },
    }
  );

/** A system's redemptions as a CSV file, oldest first, with the same access and filters. */
export const getCreditSystemRedemptionsCsv = async (
  client: ApiClient,
  systemSlug: string,
  filters: ApiCreditRedemptionAdminFilters = {}
): Promise<Blob> => {
  const response = await client.get(
    `/credits/systems/${systemSlug}/redemptions.csv`,
    {
      searchParams: {
        ...(filters.venue ? { venue: filters.venue } : {}),
        ...(filters.product ? { product: filters.product } : {}),
        ...(filters.search ? { search_q: filters.search } : {}),
      },
      returnResponse: true,
    }
  );
  return response.blob();
};

/** One member's transaction log in a system, newest first, for its admins. */
export const getCreditSystemAccountTransactions = (
  client: ApiClient,
  systemSlug: string,
  accountUuid: string,
  direction?: CreditTransactionDirection,
  page = 1
) =>
  client.get<PaginatedResponse<ApiCreditTransactionAdmin>>(
    `/credits/systems/${systemSlug}/transactions`,
    {
      searchParams: {
        account: accountUuid,
        page: String(page),
        ...(direction ? { direction } : {}),
      },
    }
  );

/**
 * Grant or take credits on a member's account, for its system's admins. A
 * grant that would go over one of the system's caps is rejected, not cut down,
 * and so is a removal of more than the member can spend. Both are a 400 whose
 * `detail.message` says how much is left, with `detail.code` of
 * `credit_cap_exceeded` or `removal_exceeds_balance`.
 */
export const createCreditSystemAccountAdjustment = (
  client: ApiClient,
  systemSlug: string,
  accountUuid: string,
  payload: ApiCreateCreditAdjustmentPayload
) =>
  client.post<ApiCreditTransactionAdmin>(
    `/credits/systems/${systemSlug}/accounts/${accountUuid}/adjustments`,
    payload
  );

/** A member's account status and the freeze or ban behind it, for staff at any of its system's venues. */
export const getCreditAccountStatus = (client: ApiClient, accountUuid: string) =>
  client.get<ApiCreditAccountStatusDetail>(`/credits/accounts/${accountUuid}`);

/** Freeze a member's account, for staff at any of its system's venues. */
export const freezeCreditAccount = (
  client: ApiClient,
  accountUuid: string,
  payload: ApiCreditAccountStatusChangePayload = {}
) =>
  client.post<ApiCreditAccountStatusChange>(
    `/credits/accounts/${accountUuid}/freeze`,
    payload
  );

/** Unfreeze a member's account, for its system's staff. Can't lift a ban. */
export const unfreezeCreditAccount = (
  client: ApiClient,
  accountUuid: string,
  payload: ApiCreditAccountStatusChangePayload = {}
) =>
  client.post<ApiCreditAccountStatusChange>(
    `/credits/accounts/${accountUuid}/unfreeze`,
    payload
  );

/**
 * Ban a member's account and forfeit its balance, for its system's admins.
 * Only Griptonite staff can lift a ban.
 */
export const banCreditAccount = (
  client: ApiClient,
  accountUuid: string,
  payload: ApiCreditAccountStatusChangePayload = {}
) =>
  client.post<ApiCreditAccountStatusChange>(
    `/credits/accounts/${accountUuid}/ban`,
    payload
  );

export const endpoints = {
  getCreditWallet,
  getCreditTransactions,
  getCreditSystemAccounts,
  getCreditSystemRedemptions,
  getCreditSystemRedemptionsCsv,
  getCreditSystemAccountTransactions,
  createCreditSystemAccountAdjustment,
  getCreditAccountStatus,
  freezeCreditAccount,
  unfreezeCreditAccount,
  banCreditAccount,
  getVenueCreditProducts,
  createCreditRedemption,
  getCreditRedemptions,
  getCreditRedemption,
  getCreditRedemptionSticker,
  getVenueCreditSystem,
  updateCreditSystem,
  getCreditSystemProducts,
  createCreditProduct,
  getCreditProduct,
  updateCreditProduct,
  deleteCreditProduct,
  getCreditProductPresetImages,
  getCreditProductVenueStock,
  setCreditProductVenueStock,
  getCreditProductCodeBatches,
  uploadCreditProductCodeBatch,
  withdrawCreditProductCodeBatch,
};
